import { getCoverEventPosterImage } from '@/features/events/shared/FocalEventPosterImage';
import { FlyerImage } from '@/features/events/shared/FlyerImage';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { Button } from '@/components/ui/button';
import { Icon } from '@/components/ui/icon';
import { Input } from '@/components/ui/input';
import { Card } from '@/components/ui/scene';
import { SmoothWebScrollView } from '@/components/ui/SmoothWebScrollView';
import { Text } from '@/components/ui/text';
import type {
  DiscoveryEvent,
  ProfileSummary,
  VenueDealCategory,
  VenuePublicDeal,
  VenueSummary,
} from '@/lib/api/apiTypes';
import { getVenueDealCategoryLabel } from '@radious/catalog/deal-categories';
import { useAuth } from '@/lib/auth/auth';
import { formatDealDays, formatDealTimeRange, getDealTiming } from '@/features/venues/deals/deals';
import { APP_FEATURES } from '@/lib/app/features';
import { useUserOriginCoords } from '@/lib/location/locationOrigin';
import { useMapEvents } from '@/features/map/useMapEvents';
import { useFeedPreferences } from '@/features/events/feed/feedPreferences';
import {
  createLoadTimer,
  trackDealSearchAppearance,
  trackDealTap,
  trackEmptyStateSeen,
  trackEventSearchAppearance,
  trackSearchQuery,
  trackSearchResultTapped,
  trackVenueSearchAppearance,
} from '@/lib/analytics/posthog';
import { createSearchCorrelationID } from '@/lib/analytics/searchAnalyticsPrivacy';
import { formatEventDayLabel, formatEventTime } from '@/features/events/eventFormatting';
import { getProfileInitials } from '@/features/profile/profileFormatting';
import { getEventHref, getPublicIdentityHref, getVenueHref } from '@/lib/navigation/routes';
import { addSentryActionBreadcrumb, createSentryInputTracker } from '@/lib/analytics/sentry';
import { type RecentSearchEntry, useRecentSearches } from '@/features/search/useRecentSearches';
import {
  buildSmartSearchResults,
  type SmartSearchResult,
} from '@/features/search/smartSearchResults';
import { SmartSearchResultRow } from '@/features/search/SmartSearchResultRow';
import {
  buildRecentDeal,
  buildRecentEvent,
  buildRecentProfile,
  buildRecentQuery,
  buildRecentVenue,
} from '@/features/search/recentSearchEntries';
import { useSearch } from '@/features/search/useSearch';
import { cn } from '@/lib/utils';
import { getVenueCategoryConfig, resolveVenueCategory } from '@/features/venues/venueCategories';
import { Redirect, Stack, router, useLocalSearchParams } from 'expo-router';
import { getResolvedFeedEntryBadgeLabel } from '@/features/events/eventMobileHelpers';
import {
  ArrowLeftIcon,
  BadgeCheckIcon,
  Building2Icon,
  CalendarDaysIcon,
  Clock3Icon,
  MapPinIcon,
  SearchIcon,
  TicketPercentIcon,
  UserRoundIcon,
  XIcon,
  type LucideIcon,
} from 'lucide-react-native';
import * as React from 'react';
import { Image, Keyboard, Pressable, View } from 'react-native';

type SearchFilter = 'all' | 'events' | 'deals' | 'venues' | 'people';

const SEARCH_FILTERS: { value: SearchFilter; label: string }[] = [
  { value: 'all', label: 'All' },
  { value: 'events', label: 'Events' },
  { value: 'deals', label: 'Deals' },
  { value: 'venues', label: 'Venues' },
  { value: 'people', label: 'Profiles' },
];

const SEARCH_SUGGESTIONS = ['Tonight', 'Live music', 'Free', 'Happy hour', 'DJ'];
const SEARCH_EMPTY_SUGGESTIONS = ['Tonight', 'Live music', 'Free', 'Happy hour'];
const SEARCH_ANALYTICS_IDLE_MS = 750;
const SEARCH_APPEARANCE_LIMIT = 5;

function getRouteQueryParam(value: string | string[] | undefined) {
  return Array.isArray(value) ? (value[0] ?? '') : (value ?? '');
}

function SearchSection({
  title,
  action,
  children,
}: {
  title: string;
  action?: React.ReactNode;
  children: React.ReactNode;
}) {
  return (
    <View className="gap-3">
      <View className="flex-row items-center justify-between gap-3">
        <Text variant="small" className="font-semibold text-foreground">
          {title}
        </Text>
        {action}
      </View>
      <View className="gap-3">{children}</View>
    </View>
  );
}

function FilterPill({
  active,
  label,
  onPress,
}: {
  active: boolean;
  label: string;
  onPress: () => void;
}) {
  return (
    <Pressable
      className={cn(
        'rounded-md border px-3 py-2 active:opacity-80',
        active
          ? 'border-primary/40 bg-primary-soft'
          : 'border-border bg-surface-alt hover:bg-surface'
      )}
      onPress={onPress}>
      <Text
        variant="small"
        className={active ? 'font-semibold text-foreground' : 'font-medium text-muted-foreground'}>
        {label}
      </Text>
    </Pressable>
  );
}

function StateCard({
  title,
  body,
  actionLabel,
  icon,
  onAction,
}: {
  title: string;
  body: string;
  actionLabel?: string;
  icon?: LucideIcon;
  onAction?: () => void;
}) {
  return (
    <Card className="rounded-2xl border-border bg-surface-alt p-5 md:px-6 md:py-6">
      <View className="gap-3">
        <View className="flex-row items-center gap-3">
          {icon ? <Icon as={icon} size={16} className="text-muted-foreground" /> : null}
          <Text className="text-lg font-semibold text-foreground">{title}</Text>
        </View>
        <Text variant="muted" className="leading-6">
          {body}
        </Text>
        {actionLabel && onAction ? (
          <View className="flex-row">
            <Button className="rounded-md px-4" onPress={onAction}>
              <Text>{actionLabel}</Text>
            </Button>
          </View>
        ) : null}
      </View>
    </Card>
  );
}

function getDealCategoryLabel(category: VenueDealCategory) {
  return getVenueDealCategoryLabel(category);
}

function getEventTimingLabel(event: DiscoveryEvent) {
  return `${formatEventDayLabel(event.starts_at)} · ${formatEventTime(event.starts_at)}`;
}

function getVenueSignals(venue: VenueSummary, hasEventSoon: boolean, hasMatchedDeal: boolean) {
  const category = resolveVenueCategory(venue.category);

  return [
    hasEventSoon ? 'Event soon' : null,
    hasMatchedDeal ? 'Deal' : null,
    category ? getVenueCategoryConfig(category).label : null,
  ].filter((signal): signal is string => Boolean(signal));
}

function SearchResultShell({
  children,
  onPress,
}: {
  children: React.ReactNode;
  onPress: () => void;
}) {
  return (
    <Pressable
      className="overflow-hidden rounded-2xl border border-border bg-surface-alt p-4 transition-opacity active:opacity-80 md:px-5"
      onPress={onPress}>
      {children}
    </Pressable>
  );
}

function EventResultCard({ event, onPress }: { event: DiscoveryEvent; onPress: () => void }) {
  const posterImage = getCoverEventPosterImage(event);
  const entryLabel = getResolvedFeedEntryBadgeLabel(event);

  return (
    <SearchResultShell onPress={onPress}>
      <View className="gap-4 md:flex-row md:items-center">
        <View className="overflow-hidden rounded-xl bg-muted md:h-[148px] md:w-[112px]">
          {posterImage ? (
            <FlyerImage
              image={posterImage}
              style={{ aspectRatio: '4 / 5', height: '100%', width: '100%' }}
            />
          ) : (
            <View className="aspect-[4/5] items-center justify-center md:h-full">
              <Icon as={CalendarDaysIcon} size={22} className="text-muted-foreground" />
            </View>
          )}
        </View>
        <View className="flex-1 gap-2.5">
          <View className="gap-1">
            <Text className="text-xl font-semibold text-foreground">{event.title}</Text>
            <Text variant="muted" className="leading-5">
              {event.venue_name || 'Venue TBD'}
            </Text>
          </View>
          <View className="flex-row flex-wrap gap-2">
            <MetaChip icon={Clock3Icon} label={getEventTimingLabel(event)} />
            {entryLabel ? <MetaChip label={entryLabel} /> : null}
          </View>
        </View>
      </View>
    </SearchResultShell>
  );
}

function DealResultCard({ deal, onPress }: { deal: VenuePublicDeal; onPress: () => void }) {
  const timing = getDealTiming(deal);
  const timingLabel =
    timing.state === 'live'
      ? 'Live now'
      : `${formatDealDays(deal.active_days)} · ${formatDealTimeRange(deal)}`;

  return (
    <SearchResultShell onPress={onPress}>
      <View className="gap-3 md:flex-row md:items-center">
        <View className="size-11 items-center justify-center rounded-lg bg-primary-soft">
          <Icon as={TicketPercentIcon} size={17} className="text-primary" />
        </View>
        <View className="flex-1 gap-2">
          <View className="gap-1">
            <Text className="text-lg font-semibold text-foreground">{deal.title}</Text>
            <Text variant="muted">{deal.venue_name}</Text>
          </View>
          <View className="flex-row flex-wrap gap-2">
            <MetaChip label={getDealCategoryLabel(deal.category)} />
            <MetaChip icon={Clock3Icon} label={timingLabel} />
          </View>
        </View>
      </View>
    </SearchResultShell>
  );
}

function ProfileResultCard({ profile, onPress }: { profile: ProfileSummary; onPress: () => void }) {
  return (
    <SearchResultShell onPress={onPress}>
      <View className="flex-row items-center gap-4">
        <Avatar alt={profile.display_name || profile.username} className="size-12 bg-primary-soft">
          {profile.avatar_url ? <AvatarImage source={{ uri: profile.avatar_url }} /> : null}
          <AvatarFallback className="bg-primary-soft">
            <Text className="text-base font-semibold text-foreground">
              {getProfileInitials(profile.display_name || profile.username)}
            </Text>
          </AvatarFallback>
        </Avatar>
        <View className="flex-1 gap-1">
          <Text className="text-lg font-semibold text-foreground">
            {profile.display_name || profile.username}
          </Text>
          <Text variant="muted">@{profile.username}</Text>
          {profile.is_venue && profile.venue_name ? (
            <Text variant="muted">Venue: {profile.venue_name}</Text>
          ) : null}
          {profile.is_limited ? (
            <Text variant="small" className="text-white/35">
              Friends only
            </Text>
          ) : null}
        </View>
        <Icon as={UserRoundIcon} size={18} className="text-muted-foreground" />
      </View>
    </SearchResultShell>
  );
}

function VenueResultCard({
  signals,
  venue,
  onPress,
}: {
  signals: string[];
  venue: VenueSummary;
  onPress: () => void;
}) {
  return (
    <SearchResultShell onPress={onPress}>
      <View className="flex-row items-start gap-4">
        {venue.logo_url ? (
          <Image
            className="size-12 rounded-full bg-muted"
            resizeMode="cover"
            source={{ uri: venue.logo_url }}
          />
        ) : (
          <View className="size-12 items-center justify-center rounded-full bg-primary-soft">
            <Icon as={Building2Icon} size={20} className="text-primary" />
          </View>
        )}
        <View className="flex-1 gap-2">
          <View className="flex-row items-center gap-1.5">
            <Text className="text-lg font-semibold text-foreground">{venue.name}</Text>
            {venue.is_verified ? (
              <Icon as={BadgeCheckIcon} size={16} className="text-primary" />
            ) : null}
          </View>
          {venue.address ? (
            <View className="flex-row items-center gap-2">
              <Icon as={MapPinIcon} size={14} className="text-muted-foreground" />
              <Text variant="muted" className="flex-1 leading-5">
                {venue.address}
              </Text>
            </View>
          ) : null}
          {signals.length > 0 ? (
            <View className="flex-row flex-wrap gap-2">
              {signals.map((signal) => (
                <MetaChip key={signal} label={signal} />
              ))}
            </View>
          ) : null}
        </View>
      </View>
    </SearchResultShell>
  );
}

function MetaChip({ icon, label }: { icon?: LucideIcon; label: string }) {
  return (
    <View className="flex-row items-center gap-1.5 rounded-md border border-border px-2.5 py-1.5">
      {icon ? <Icon as={icon} size={12} className="text-muted-foreground" /> : null}
      <Text variant="small" className="font-medium text-muted-foreground">
        {label}
      </Text>
    </View>
  );
}

function RecentSearchRow({
  entry,
  isLast,
  onDismiss,
  onPress,
}: {
  entry: RecentSearchEntry;
  isLast: boolean;
  onDismiss: () => void;
  onPress: () => void;
}) {
  return (
    <View
      className={cn('flex-row items-center gap-3 px-4 py-3', !isLast && 'border-b border-border')}>
      <Pressable className="flex-1 flex-row items-center gap-3 active:opacity-80" onPress={onPress}>
        <View className="size-10 items-center justify-center rounded-lg bg-primary-soft">
          <Icon
            as={
              entry.type === 'event'
                ? CalendarDaysIcon
                : entry.type === 'deal'
                  ? TicketPercentIcon
                  : entry.type === 'venue'
                    ? Building2Icon
                    : entry.type === 'profile'
                      ? UserRoundIcon
                      : SearchIcon
            }
            size={17}
            className="text-primary"
          />
        </View>
        <View className="flex-1">
          <Text className="font-semibold text-foreground">{entry.title}</Text>
          {entry.subtitle ? <Text variant="muted">{entry.subtitle}</Text> : null}
        </View>
      </Pressable>
      <Pressable
        accessibilityLabel={`Remove ${entry.title} from recent searches`}
        className="size-8 items-center justify-center rounded-md border border-border bg-background active:opacity-80"
        onPress={onDismiss}>
        <Icon as={XIcon} size={16} className="text-muted-foreground" />
      </Pressable>
    </View>
  );
}

export default function SearchWebScreen() {
  const params = useLocalSearchParams<{ q?: string | string[] }>();
  const session = useAuth((state) => state.session);
  const userOrigin = useUserOriginCoords();
  const initialQuery = React.useMemo(() => getRouteQueryParam(params.q), [params.q]);
  const setFeedTab = useFeedPreferences((state) => state.setFeedTab);
  const setTimeWindow = useFeedPreferences((state) => state.setTimeWindow);
  const [query, setQuery] = React.useState(initialQuery);
  const [filterState, setFilterState] = React.useState<{
    query: string;
    value: SearchFilter;
  }>(() => ({ query: initialQuery.trim(), value: 'all' }));
  const searchQuery = useSearch(query, APP_FEATURES.search, 12);
  const soonEventsQuery = useMapEvents({
    enabled: APP_FEATURES.search && query.trim().length < 2,
    latitude: userOrigin.latitude,
    limit: 6,
    longitude: userOrigin.longitude,
    originLatitude: userOrigin.latitude,
    originLongitude: userOrigin.longitude,
    sort: 'soonest',
    timeWindow: 'live',
  });
  const { recentSearches, saveSearch, clearSearches, removeSearch, isHydrated } =
    useRecentSearches();
  const userCounty = session?.me.profile?.last_county_name ?? null;
  const loadTimerRef = React.useRef(createLoadTimer('search'));
  const searchSessionKeyRef = React.useRef(`${Date.now()}:${Math.random().toString(36).slice(2)}`);
  const appearedSearchResultsRef = React.useRef(new Set<string>());
  const searchInputTracker = React.useMemo(
    () => createSentryInputTracker({ screen: 'web_search', field: 'query', form: 'global_search' }),
    []
  );

  const trimmedQuery = query.trim();
  const searchCorrelationID = React.useMemo(createSearchCorrelationID, [trimmedQuery]);
  const activeFilter = filterState.query === trimmedQuery ? filterState.value : 'all';
  const updateActiveFilter = React.useCallback(
    (value: SearchFilter) => setFilterState({ query: trimmedQuery, value }),
    [trimmedQuery]
  );
  const events = React.useMemo(() => searchQuery.data?.events ?? [], [searchQuery.data?.events]);
  const deals = React.useMemo(() => searchQuery.data?.deals ?? [], [searchQuery.data?.deals]);
  const profiles = React.useMemo(
    () => (searchQuery.data?.profiles ?? []).filter((profile) => !profile.is_venue),
    [searchQuery.data?.profiles]
  );
  const venues = React.useMemo(() => searchQuery.data?.venues ?? [], [searchQuery.data?.venues]);
  const soonEvents = React.useMemo(
    () => soonEventsQuery.data?.events ?? [],
    [soonEventsQuery.data?.events]
  );
  const eventVenueIDs = React.useMemo(
    () =>
      new Set(
        [...events, ...soonEvents].flatMap((event) => (event.venue_id ? [event.venue_id] : []))
      ),
    [events, soonEvents]
  );
  const dealVenueIDs = React.useMemo(() => new Set(deals.map((deal) => deal.venue_id)), [deals]);
  const visibleEvents = activeFilter === 'all' || activeFilter === 'events' ? events : [];
  const visibleDeals = activeFilter === 'all' || activeFilter === 'deals' ? deals : [];
  const visibleProfiles = React.useMemo(
    () => (activeFilter === 'all' || activeFilter === 'people' ? profiles : []),
    [activeFilter, profiles]
  );
  const visibleVenues = activeFilter === 'all' || activeFilter === 'venues' ? venues : [];
  const topResults = React.useMemo(
    () =>
      buildSmartSearchResults({
        deals,
        events,
        profiles,
        query: trimmedQuery,
        venues,
      }),
    [deals, events, profiles, trimmedQuery, venues]
  );
  const hasResults =
    events.length > 0 || deals.length > 0 || profiles.length > 0 || venues.length > 0;
  const hasVisibleResults =
    visibleEvents.length > 0 ||
    visibleDeals.length > 0 ||
    visibleProfiles.length > 0 ||
    visibleVenues.length > 0;
  const hasRecentSearches = recentSearches.length > 0;

  React.useEffect(() => {
    setQuery(initialQuery);
  }, [initialQuery]);

  React.useEffect(() => {
    loadTimerRef.current = createLoadTimer('search');
  }, [trimmedQuery]);

  React.useEffect(() => {
    if (typeof window === 'undefined') {
      return;
    }

    const timeoutId = window.setTimeout(() => {
      const nextURL = new URL(window.location.href);
      if (trimmedQuery) {
        nextURL.searchParams.set('q', trimmedQuery);
      } else {
        nextURL.searchParams.delete('q');
      }

      if (nextURL.toString() !== window.location.href) {
        window.history.replaceState({}, '', nextURL.toString());
      }
    }, 150);

    return () => window.clearTimeout(timeoutId);
  }, [trimmedQuery]);

  React.useEffect(() => {
    if (trimmedQuery.length < 2 || searchQuery.isPending) {
      return;
    }

    loadTimerRef.current.done();

    if (searchQuery.isError) {
      const timeout = setTimeout(() => {
        trackEmptyStateSeen({
          screen: 'search',
          userCounty,
          reason: 'error',
        });
      }, SEARCH_ANALYTICS_IDLE_MS);
      return () => clearTimeout(timeout);
    }

    const timeout = setTimeout(() => {
      trackSearchQuery({
        query: trimmedQuery,
        resultsCount: events.length + deals.length + profiles.length + venues.length,
        searchCorrelationID,
        userCounty,
        sessionKey: searchSessionKeyRef.current,
      });

      events.slice(0, SEARCH_APPEARANCE_LIMIT).forEach((event, index) => {
        const key = `${searchCorrelationID}:event:${event.id}:${index}`;
        if (appearedSearchResultsRef.current.has(key)) {
          return;
        }
        appearedSearchResultsRef.current.add(key);
        trackEventSearchAppearance({
          eventID: event.id,
          position: index + 1,
          query: trimmedQuery,
          searchCorrelationID,
          sessionKey: searchSessionKeyRef.current,
          venueID: event.venue_id ?? null,
        });
      });

      deals.slice(0, SEARCH_APPEARANCE_LIMIT).forEach((deal, index) => {
        const key = `${searchCorrelationID}:deal:${deal.id}:${index}`;
        if (appearedSearchResultsRef.current.has(key)) {
          return;
        }
        appearedSearchResultsRef.current.add(key);
        trackDealSearchAppearance({
          dealID: deal.id,
          position: index + 1,
          query: trimmedQuery,
          searchCorrelationID,
          sessionKey: searchSessionKeyRef.current,
          venueID: deal.venue_id,
        });
      });

      venues.slice(0, SEARCH_APPEARANCE_LIMIT).forEach((venue, index) => {
        const key = `${searchCorrelationID}:venue:${venue.id}:${index}`;
        if (appearedSearchResultsRef.current.has(key)) {
          return;
        }
        appearedSearchResultsRef.current.add(key);
        trackVenueSearchAppearance({
          position: index + 1,
          query: trimmedQuery,
          searchCorrelationID,
          sessionKey: searchSessionKeyRef.current,
          venueID: venue.id,
        });
      });

      if (!hasResults) {
        trackEmptyStateSeen({
          screen: 'search',
          userCounty,
          reason: 'no_results',
        });
      }
    }, SEARCH_ANALYTICS_IDLE_MS);

    return () => clearTimeout(timeout);
  }, [
    deals,
    deals.length,
    events,
    events.length,
    hasResults,
    profiles.length,
    searchQuery.isError,
    searchQuery.isPending,
    searchCorrelationID,
    trimmedQuery,
    userCounty,
    venues,
    venues.length,
  ]);

  const openRecentSearch = React.useCallback(
    (entry: RecentSearchEntry) => {
      addSentryActionBreadcrumb({
        screen: 'web_search',
        action: 'open_recent_search',
        data: {
          entry_type: entry.type,
          has_route: Boolean(entry.route),
          has_query: Boolean(entry.query),
        },
      });

      void saveSearch(entry);

      if (entry.route) {
        router.navigate(entry.route);
        return;
      }

      if (entry.query) {
        setQuery(entry.query);
      }
    },
    [saveSearch]
  );

  const runSearchQuery = React.useCallback(
    (nextQuery: string) => {
      const nextTrimmedQuery = nextQuery.trim();
      if (!nextTrimmedQuery) {
        return;
      }

      setQuery(nextTrimmedQuery);
      void saveSearch(buildRecentQuery(nextTrimmedQuery));
    },
    [saveSearch]
  );

  const openNearbyEvents = React.useCallback(() => {
    setFeedTab('nearby');
    setTimeWindow('today');
    Keyboard.dismiss();
    router.navigate('/events');
  }, [setFeedTab, setTimeWindow]);

  const saveCurrentQuery = React.useCallback(() => {
    if (trimmedQuery.length >= 2) {
      void saveSearch(buildRecentQuery(trimmedQuery));
    }
  }, [saveSearch, trimmedQuery]);

  const openEventResult = React.useCallback(
    (event: DiscoveryEvent, position: number) => {
      addSentryActionBreadcrumb({
        screen: 'web_search',
        action: 'open_event_result',
        data: {
          query_length: trimmedQuery.length,
          event_id: event.id,
          position,
        },
      });
      trackSearchResultTapped({
        query: trimmedQuery,
        position,
        eventID: event.id,
        searchCorrelationID,
        targetType: 'event',
        venueID: event.venue_id ?? null,
      });
      saveCurrentQuery();
      void saveSearch(buildRecentEvent(event));
      Keyboard.dismiss();
      router.navigate(getEventHref(event.id, event.title) as never);
    },
    [saveCurrentQuery, saveSearch, searchCorrelationID, trimmedQuery]
  );

  const openDealResult = React.useCallback(
    (deal: VenuePublicDeal, position: number) => {
      addSentryActionBreadcrumb({
        screen: 'web_search',
        action: 'open_deal_result',
        data: {
          query_length: trimmedQuery.length,
          deal_id: deal.id,
          venue_id: deal.venue_id,
          position,
        },
      });
      trackSearchResultTapped({
        dealID: deal.id,
        query: trimmedQuery,
        position,
        searchCorrelationID,
        targetType: 'deal',
        venueID: deal.venue_id,
      });
      trackDealTap({
        dealID: deal.id,
        position,
        source: 'search',
        surface: 'web',
        venueID: deal.venue_id,
      });
      saveCurrentQuery();
      void saveSearch(buildRecentDeal(deal));
      Keyboard.dismiss();
      router.navigate(
        getVenueHref(deal.venue_id, { deal: deal.id, source: 'search' }, { name: deal.venue_name })
      );
    },
    [saveCurrentQuery, saveSearch, searchCorrelationID, trimmedQuery]
  );

  const openVenueResult = React.useCallback(
    (venue: VenueSummary, position: number) => {
      addSentryActionBreadcrumb({
        screen: 'web_search',
        action: 'open_venue_result',
        data: {
          query_length: trimmedQuery.length,
          venue_id: venue.id,
          position,
        },
      });
      trackSearchResultTapped({
        query: trimmedQuery,
        position,
        searchCorrelationID,
        targetType: 'venue',
        venueID: venue.id,
      });
      saveCurrentQuery();
      void saveSearch(buildRecentVenue(venue));
      Keyboard.dismiss();
      router.navigate(getVenueHref(venue.id, { source: 'search' }, { name: venue.name }));
    },
    [saveCurrentQuery, saveSearch, searchCorrelationID, trimmedQuery]
  );

  const openProfileResult = React.useCallback(
    (profile: ProfileSummary, positionOverride?: number) => {
      const position =
        positionOverride ??
        visibleProfiles.findIndex((candidate) => candidate.user_id === profile.user_id) + 1;

      addSentryActionBreadcrumb({
        screen: 'web_search',
        action: 'open_profile_result',
        data: {
          query_length: trimmedQuery.length,
          profile_id: profile.user_id,
          position,
        },
      });
      trackSearchResultTapped({
        query: trimmedQuery,
        position,
        searchCorrelationID,
        targetType: 'profile',
        venueID: profile.venue_id ?? null,
      });
      saveCurrentQuery();
      void saveSearch(buildRecentProfile(profile));
      Keyboard.dismiss();
      router.navigate(getPublicIdentityHref(profile));
    },
    [saveCurrentQuery, saveSearch, searchCorrelationID, trimmedQuery, visibleProfiles]
  );

  const openTopResult = React.useCallback(
    (result: SmartSearchResult, index: number) => {
      const position = index + 1;

      switch (result.type) {
        case 'event':
          openEventResult(result.item, position);
          return;
        case 'deal':
          openDealResult(result.item, position);
          return;
        case 'profile':
          openProfileResult(result.item, position);
          return;
        case 'venue':
          openVenueResult(result.item, position);
          return;
      }
    },
    [openDealResult, openEventResult, openProfileResult, openVenueResult]
  );

  if (!APP_FEATURES.search) {
    return <Redirect href="/events" />;
  }

  return (
    <View className="min-h-screen flex-1 bg-background">
      <Stack.Screen
        options={{
          headerShown: false,
          title: trimmedQuery ? `${trimmedQuery} · Search` : 'Search',
        }}
      />
      <SmoothWebScrollView
        className="flex-1"
        contentContainerClassName="min-h-screen px-4 pb-10 pt-6 md:px-8 md:pb-16 md:pt-10">
        <View className="mx-auto w-full max-w-[1120px] gap-6">
          <View className="gap-4 border-b border-border/70 pb-5">
            <View className="flex-col gap-4 md:flex-row md:items-start md:justify-between">
              <View className="gap-1.5">
                <Text className="text-[30px] font-semibold tracking-tight text-foreground md:text-[36px]">
                  Search
                </Text>
                <Text variant="muted" className="max-w-[620px] leading-6">
                  Events, deals, venues, and public profiles across San Antonio, with a URL you can
                  share.
                </Text>
              </View>
              <Button
                variant="outline"
                className="rounded-md px-3"
                onPress={() => router.push('/events')}>
                <Icon as={ArrowLeftIcon} size={16} className="text-foreground" />
                <Text>Back to Events</Text>
              </Button>
            </View>

            <View className="gap-3 md:flex-row md:items-center">
              <View className="min-w-0 flex-1 rounded-2xl border border-border bg-surface-alt p-3">
                <View className="flex-row items-center gap-3">
                  <Icon as={SearchIcon} size={18} className="text-muted-foreground" />
                  <Input
                    autoCapitalize="none"
                    autoCorrect={false}
                    autoFocus
                    className="h-10 flex-1 border-0 bg-transparent px-0 text-base shadow-none focus-visible:ring-0 dark:bg-transparent"
                    style={{ backgroundColor: 'transparent' }}
                    onBlur={() => searchInputTracker.blur(query)}
                    onChangeText={(value) => {
                      searchInputTracker.changeText(value);
                      setQuery(value);
                    }}
                    onFocus={() => searchInputTracker.focus()}
                    onSubmitEditing={() => {
                      searchInputTracker.submit(query);
                      if (trimmedQuery.length >= 2) {
                        void saveSearch(buildRecentQuery(trimmedQuery));
                      }
                    }}
                    placeholder="Search events, deals, profiles, or venues"
                    returnKeyType="search"
                    value={query}
                  />
                  {trimmedQuery ? (
                    <Pressable
                      accessibilityLabel="Clear search"
                      className="size-8 items-center justify-center rounded-md active:opacity-80"
                      onPress={() => setQuery('')}>
                      <Icon as={XIcon} size={15} className="text-muted-foreground" />
                    </Pressable>
                  ) : null}
                </View>
              </View>

              <View className="flex-row flex-wrap gap-2 md:justify-end">
                {SEARCH_FILTERS.map((filter) => (
                  <FilterPill
                    key={filter.value}
                    active={activeFilter === filter.value}
                    label={filter.label}
                    onPress={() => updateActiveFilter(filter.value)}
                  />
                ))}
              </View>
            </View>
          </View>

          {trimmedQuery.length < 2 ? (
            <View className="gap-6 md:flex-row md:items-start">
              <View className="gap-6 md:min-w-0 md:flex-[1.35]">
                <SearchSection title="Popular right now">
                  <View className="flex-row flex-wrap gap-2">
                    {SEARCH_SUGGESTIONS.map((suggestion) => (
                      <Pressable
                        key={suggestion}
                        className="rounded-md border border-border bg-surface-alt px-3.5 py-2 active:opacity-80"
                        onPress={() => runSearchQuery(suggestion)}>
                        <Text variant="small" className="font-medium text-foreground">
                          {suggestion}
                        </Text>
                      </Pressable>
                    ))}
                    <Pressable
                      className="rounded-md border border-primary/40 bg-primary-soft px-3.5 py-2 active:opacity-80"
                      onPress={openNearbyEvents}>
                      <Text variant="small" className="font-semibold text-foreground">
                        Near me
                      </Text>
                    </Pressable>
                  </View>
                </SearchSection>

                {soonEvents.length > 0 ? (
                  <SearchSection title="Happening Soon">
                    {soonEvents.slice(0, 4).map((event, index) => (
                      <EventResultCard
                        key={event.id}
                        event={event}
                        onPress={() => openEventResult(event, index + 1)}
                      />
                    ))}
                  </SearchSection>
                ) : null}
              </View>

              <View className="gap-6 md:w-[340px] md:max-w-[340px] md:flex-1">
                {isHydrated && hasRecentSearches ? (
                  <SearchSection
                    title="Recent Searches"
                    action={
                      <Pressable onPress={() => void clearSearches()}>
                        <Text variant="small" className="font-semibold text-primary">
                          Clear
                        </Text>
                      </Pressable>
                    }>
                    <Card className="rounded-2xl border-border bg-surface-alt p-0">
                      {recentSearches.map((entry, index) => (
                        <RecentSearchRow
                          key={entry.key}
                          entry={entry}
                          isLast={index === recentSearches.length - 1}
                          onDismiss={() => void removeSearch(entry.key)}
                          onPress={() => openRecentSearch(entry)}
                        />
                      ))}
                    </Card>
                  </SearchSection>
                ) : (
                  <StateCard
                    body="Open an event, deal, venue, or profile from search and it will stay handy here on this browser."
                    icon={SearchIcon}
                    title="Recent searches"
                  />
                )}
              </View>
            </View>
          ) : null}

          {trimmedQuery.length >= 2 && searchQuery.isPending ? (
            <StateCard
              body="Looking across upcoming events, deals, public profiles, and venues."
              icon={SearchIcon}
              title="Searching..."
            />
          ) : null}

          {trimmedQuery.length >= 2 && searchQuery.isError ? (
            <StateCard
              actionLabel="Try again"
              body="Search is having trouble right now. Please try again in a moment."
              icon={SearchIcon}
              onAction={() => searchQuery.refetch()}
              title="Search unavailable"
            />
          ) : null}

          {trimmedQuery.length >= 2 &&
          !searchQuery.isPending &&
          !searchQuery.isError &&
          !hasVisibleResults ? (
            <StateCard
              body={
                hasResults
                  ? 'No matches in this filter. Try All or switch to another result type.'
                  : 'Try a venue, event, deal, profile, or one of these searches.'
              }
              actionLabel={hasResults ? undefined : 'See nearby events'}
              icon={SearchIcon}
              onAction={hasResults ? undefined : openNearbyEvents}
              title="No matches yet"
            />
          ) : null}

          {trimmedQuery.length >= 2 &&
          !searchQuery.isPending &&
          !searchQuery.isError &&
          !hasVisibleResults &&
          !hasResults ? (
            <View className="flex-row flex-wrap gap-2">
              {SEARCH_EMPTY_SUGGESTIONS.map((suggestion) => (
                <Pressable
                  key={suggestion}
                  className="rounded-md border border-border bg-surface-alt px-3.5 py-2 active:opacity-80"
                  onPress={() => runSearchQuery(suggestion)}>
                  <Text variant="small" className="font-medium text-foreground">
                    {suggestion}
                  </Text>
                </Pressable>
              ))}
              <Pressable
                className="rounded-md border border-primary/40 bg-primary-soft px-3.5 py-2 active:opacity-80"
                onPress={openNearbyEvents}>
                <Text variant="small" className="font-semibold text-foreground">
                  Near me
                </Text>
              </Pressable>
            </View>
          ) : null}

          {activeFilter === 'all' && topResults.length > 0 ? (
            <SearchSection title="Top Results">
              {topResults.map((result, index) => (
                <SmartSearchResultRow
                  key={result.key}
                  result={result}
                  onPress={() => openTopResult(result, index)}
                />
              ))}
            </SearchSection>
          ) : null}

          {visibleEvents.length > 0 ? (
            <SearchSection title="Events">
              {visibleEvents.map((event, index) => (
                <EventResultCard
                  key={event.id}
                  event={event}
                  onPress={() => openEventResult(event, index + 1)}
                />
              ))}
            </SearchSection>
          ) : null}

          {visibleDeals.length > 0 ? (
            <SearchSection title="Deals">
              {visibleDeals.map((deal, index) => (
                <DealResultCard
                  key={deal.id}
                  deal={deal}
                  onPress={() => openDealResult(deal, index + 1)}
                />
              ))}
            </SearchSection>
          ) : null}

          {visibleProfiles.length > 0 ? (
            <SearchSection title="Profiles">
              {visibleProfiles.map((profile) => (
                <ProfileResultCard
                  key={profile.user_id}
                  onPress={() => openProfileResult(profile)}
                  profile={profile}
                />
              ))}
            </SearchSection>
          ) : null}

          {visibleVenues.length > 0 ? (
            <SearchSection title="Venues">
              {visibleVenues.map((venue, index) => (
                <VenueResultCard
                  key={venue.id}
                  onPress={() => openVenueResult(venue, index + 1)}
                  signals={getVenueSignals(
                    venue,
                    eventVenueIDs.has(venue.id),
                    dealVenueIDs.has(venue.id)
                  )}
                  venue={venue}
                />
              ))}
            </SearchSection>
          ) : null}
        </View>
      </SmoothWebScrollView>
    </View>
  );
}

export function ErrorBoundary({ retry }: { error: Error; retry: () => void }) {
  return (
    <View className="min-h-screen flex-1 items-center justify-center bg-background px-4">
      <View className="w-full max-w-[540px]">
        <StateCard
          actionLabel="Try again"
          body="Something got tangled while loading web search. Try again, or head back to events and reopen search."
          icon={SearchIcon}
          onAction={retry}
          title="Search hit a snag"
        />
      </View>
    </View>
  );
}
